import React from "react";

/**
 * FrostDates — frost_dates evidence component.
 *
 * Mirrors the ComparisonPair layout (Forecast Truth: "Days the forecast was
 * wrong in your favour") — large figures separated by vertical dividers — but
 * adapted for three stats instead of two:
 *
 *   First freeze · Last freeze · Freeze days/yr
 *
 * Null freeze fields → "Rarely freezes" rather than blanks (spec §7).
 * When all three are null/zero, collapse to a single centered "Rarely freezes"
 * notice instead of three empty columns.
 *
 * Spec references: §7 (frost_dates), §2d (formatting).
 */

const fmtNum = (v) => Number(v ?? 0).toLocaleString();

const FrostDates = ({ panel }) => {
  const f = panel.facts || {};
  const firstFreeze = f.typical_first_freeze;
  const lastFreeze = f.typical_last_freeze;
  const freezeDays = Number(f.freeze_days_per_year) || 0;

  // Mild climate — no freeze data at all. Show one centered notice.
  const allEmpty = !firstFreeze && !lastFreeze && freezeDays === 0;
  if (allEmpty) {
    return (
      <div className="text-center py-2">
        <span className="text-2xl font-extrabold text-emerald-600 dark:text-emerald-400 leading-none">
          Rarely freezes
        </span>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          This property is in a mild climate — winterising is rarely needed.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Three stats side by side with dividers — mirrors ComparisonPair */}
      <div className="flex items-baseline justify-center gap-4 sm:gap-6">
        {/* First freeze */}
        <div className="text-center flex-1">
          <span className="text-3xl font-bold text-blue-500 dark:text-blue-400 leading-none">
            {firstFreeze || "Rarely"}
          </span>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
            First freeze
          </p>
        </div>

        <div className="w-px h-12 bg-gray-200 dark:bg-gray-700 self-center" />

        {/* Last freeze */}
        <div className="text-center flex-1">
          <span className="text-3xl font-bold text-blue-500 dark:text-blue-400 leading-none">
            {lastFreeze || "Rarely"}
          </span>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
            Last freeze
          </p>
        </div>

        <div className="w-px h-12 bg-gray-200 dark:bg-gray-700 self-center" />

        {/* Freeze days/yr */}
        <div className="text-center flex-1">
          <span className="text-3xl font-bold text-gray-900 dark:text-gray-100 leading-none">
            {freezeDays > 0 ? fmtNum(freezeDays) : "0"}
          </span>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
            Freeze days/yr
          </p>
        </div>
      </div>

      {/* FYI-style muted caption — winterising context */}
      {firstFreeze && lastFreeze && (
        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-3 text-center italic">
          Winterise between {firstFreeze} and {lastFreeze}
        </p>
      )}
    </div>
  );
};

export default FrostDates;
